<template>
  <section class="ai-section ai-grid">
    <aside class="ai-panel ai-panel--sidebar">
      <div class="ai-panel__header">
        <div>
          <h2 class="ai-panel__title">Runs</h2>
          <p class="ai-panel__subtitle">Istoricul execuțiilor async din pipeline-ul AI.</p>
        </div>
        <v-btn icon variant="text" size="small" :loading="runsLoading" @click="refreshPanel">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <div v-if="runsLoading" class="ai-loading">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <div v-else-if="!runs.length" class="ai-empty">
        Nu există runs AI de afișat.
      </div>
      <div v-else class="ai-list">
        <button
          v-for="run in runs"
          :key="run.id"
          type="button"
          class="ai-list-card"
          :class="{ 'ai-list-card--active': selectedRunId === run.id }"
          @click="selectRun(run.id)"
        >
          <div class="ai-list-card__meta">
            <span class="ai-list-card__entity">{{ formatEntityLabel(run.entity_type, run.entity_id) }}</span>
            <v-chip :color="runStatusColor(run.status)">
              {{ formatRunStatus(run.status) }}
            </v-chip>
          </div>
          <strong class="ai-list-card__title">
            {{ run.profile_key }} · {{ run.provider_key }}/{{ run.model_key }}
          </strong>
          <p v-if="run.status === 'failed' && summarizeRunErrorMessage(run.last_error_message)" class="ai-list-card__error">
            {{ summarizeRunErrorMessage(run.last_error_message) }}
          </p>
          <div class="ai-list-card__footer">
            <span>Attempts {{ run.attempt_count }}/{{ run.max_attempts }}</span>
            <span>{{ run.created_at }}</span>
          </div>
        </button>
      </div>
    </aside>

    <div class="ai-panel ai-panel--content">
      <div v-if="runDetailLoading" class="ai-loading">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <div v-else-if="!runDetail" class="ai-empty ai-empty--large">
        Selectează un run pentru a vedea payload-ul și attempts-urile.
      </div>
      <div v-else class="ai-run-detail">
        <div class="ai-panel__header">
          <div>
            <h2 class="ai-panel__title">{{ runDetail.run.profile_key }}</h2>
            <p class="ai-panel__subtitle">
              {{ formatEntityLabel(runDetail.run.entity_type, runDetail.run.entity_id) }}
            </p>
          </div>
          <div class="ai-action-row">
            <v-chip :color="runStatusColor(runDetail.run.status)">
              {{ formatRunStatus(runDetail.run.status) }}
            </v-chip>
            <v-btn
              variant="tonal"
              color="primary"
              :disabled="!RUN_RETRYABLE_STATUSES.includes(runDetail.run.status)"
              :loading="runRetryLoading"
              @click="retryRun"
            >
              Retry run
            </v-btn>
          </div>
        </div>

        <div v-if="runDetail.run.last_error_message" class="ai-summary-card ai-summary-card--error">
          <span class="ai-summary-card__label">Ultima eroare run</span>
          <pre class="ai-summary-card__value">{{ runDetail.run.last_error_message }}</pre>
        </div>

        <AiRunObservabilityCard
          v-if="runDetail.observability"
          :observability="runDetail.observability"
        />

        <div class="ai-summary-card">
          <span class="ai-summary-card__label">Rezultat run</span>
          <pre class="ai-summary-card__value">{{ formatJsonBlock(runDetail.run.result_json) }}</pre>
        </div>

        <div class="ai-summary-card">
          <span class="ai-summary-card__label">Attempts</span>
          <div v-if="runDetail.attempts?.length" class="ai-attempts">
            <AiRunAttemptCard v-for="attempt in runDetail.attempts" :key="attempt.id" :attempt="attempt" />
          </div>
          <span v-else class="ai-muted">Nu există attempts salvate.</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import axios from "axios";
import AiRunAttemptCard from "./AiRunAttemptCard.vue";
import AiRunObservabilityCard from "./AiRunObservabilityCard.vue";
import { summarizeRunErrorMessage } from "./aiRunObservability.mjs";
import {
  RUN_RETRYABLE_STATUSES,
  formatJsonBlock,
  formatEntityLabel,
  formatRunStatus,
  runStatusColor,
} from "./aiWorkspaceShared.mjs";

export default {
  name: "AiRunsPanel",
  components: {
    AiRunAttemptCard,
    AiRunObservabilityCard,
  },
  emits: ["feedback"],
  data() {
    return {
      RUN_RETRYABLE_STATUSES,
      runs: [],
      runsLoading: false,
      runDetail: null,
      runDetailLoading: false,
      selectedRunId: null,
      runRetryLoading: false,
    };
  },
  watch: {
    "$route.query.runId"(value) {
      const nextRunId = value ? Number(value) : null;
      if (nextRunId && nextRunId !== this.selectedRunId) {
        this.selectRun(nextRunId, false);
      }
    },
  },
  mounted() {
    this.loadRuns(this.resolveRouteRunId());
  },
  methods: {
    formatJsonBlock,
    formatEntityLabel,
    formatRunStatus,
    runStatusColor,
    summarizeRunErrorMessage,
    emitFeedback(type, message) {
      this.$emit("feedback", { type, message });
    },
    refreshPanel() {
      return this.loadRuns(this.selectedRunId || this.resolveRouteRunId());
    },
    resolveRouteRunId() {
      return this.$route.query?.runId ? Number(this.$route.query.runId) : null;
    },
    resolveTargetRunId(preferredRunId = null) {
      const availableIds = new Set(this.runs.map((run) => Number(run.id)));
      const candidateIds = [preferredRunId, this.selectedRunId, this.resolveRouteRunId(), this.runs?.[0]?.id];

      for (const candidate of candidateIds) {
        const normalized = Number(candidate);
        if (Number.isFinite(normalized) && availableIds.has(normalized)) {
          return normalized;
        }
      }
      return null;
    },
    async loadRuns(preferredRunId = null) {
      this.runsLoading = true;
      try {
        const { data } = await axios.get("/ai/runs");
        this.runs = Array.isArray(data?.items) ? data.items : [];

        const targetRunId = this.resolveTargetRunId(preferredRunId);
        if (targetRunId) {
          await this.selectRun(targetRunId, false);
          return;
        }

        this.selectedRunId = null;
        this.runDetail = null;
      } catch (error) {
        this.emitFeedback("error", error?.response?.data?.message || "Nu s-a putut încărca lista de runs.");
      } finally {
        this.runsLoading = false;
      }
    },
    async selectRun(runId, updateRoute = true) {
      if (!runId) {
        return;
      }

      this.selectedRunId = Number(runId);
      if (updateRoute) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            tab: "runs",
            runId: String(runId),
          },
        });
      }
      this.runDetailLoading = true;
      try {
        const { data } = await axios.get(`/ai/runs/${runId}`);
        this.runDetail = data;
      } catch (error) {
        this.emitFeedback("error", error?.response?.data?.message || "Nu s-au putut încărca detaliile run-ului.");
      } finally {
        this.runDetailLoading = false;
      }
    },
    async retryRun() {
      if (!this.runDetail?.run?.id) {
        return;
      }

      this.runRetryLoading = true;
      try {
        await axios.post(`/ai/runs/${this.runDetail.run.id}/retry`);
        this.emitFeedback("success", "Run-ul AI a fost trimis din nou în coadă.");
        await this.loadRuns();
      } catch (error) {
        this.emitFeedback("error", error?.response?.data?.message || "Nu s-a putut relansa run-ul.");
      } finally {
        this.runRetryLoading = false;
      }
    },
  },
};
</script>
